import { pb, PB_URL, USERS_COLLECTION } from "./pb";

// The sign-in methods a deployment actually offers.
//
// Discovered at runtime rather than compiled in, because which ones exist is a
// deployment decision: a customer with their own identity provider gets an OIDC
// button, one without Google credentials must not be shown a Google button that
// fails. Hard-coding the buttons would mean a broken sign-in page on every
// environment configured differently from the developer's.

export interface OAuthMethod {
  name: string;
  displayName: string;
  /** False when the deployment intends to offer this but has no credentials yet. */
  configured: boolean;
}

export interface SignInMethods {
  password: boolean;
  otp: boolean;
  passkey: boolean;
  oauth: OAuthMethod[];
}

// Discovery is cached for the tab's lifetime. Which methods a deployment offers
// changes at deploy time, not between two clicks, so re-asking on every
// navigation spends a round trip to learn what we already know.
const CACHE_KEY = "authentrace.signin-methods.v1";

/** What to show before discovery lands: the near-certain case, immediately. */
export const OPTIMISTIC: SignInMethods = {
  password: true,
  otp: false,
  passkey: false,
  oauth: [],
};

/** A cached answer from earlier in this tab, if there is one. */
export function cachedSignInMethods(): SignInMethods | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as SignInMethods) : null;
  } catch {
    return null;
  }
}

/**
 * Ask the server what it offers.
 *
 * All three requests go out TOGETHER. They were sequential, which cost three
 * round trips — about a second through a tunnel — before the page rendered
 * anything at all. They are independent, so serialising them bought nothing
 * except a spinner.
 */
export async function loadSignInMethods(): Promise<SignInMethods> {
  const methods: SignInMethods = { password: true, otp: false, passkey: false, oauth: [] };

  const [listed, declared, passkey] = await Promise.allSettled([
    pb.collection(USERS_COLLECTION).listAuthMethods(),
    fetch(`${PB_URL}/api/dpp/auth/providers`).then((r) => (r.ok ? r.json() : null)),
    fetch(`${PB_URL}/api/dpp/passkey/available`).then((r) => (r.ok ? r.json() : null)),
  ]);

  // Each result is handled on its own. One failing discovery call must not
  // blank the sign-in page — a page offering the method everybody has beats a
  // page offering nothing.
  if (listed.status === "fulfilled") {
    const list = listed.value;
    methods.password = list.password?.enabled ?? true;
    methods.otp = list.otp?.enabled ?? false;
    methods.oauth = (list.oauth2?.providers ?? []).map((p) => ({
      name: p.name,
      displayName: p.displayName || titleCase(p.name),
      configured: true,
    }));
  }
  if (declared.status === "fulfilled" && declared.value) {
    const known = new Set(methods.oauth.map((p) => p.name));
    for (const d of (declared.value.providers ?? []) as OAuthMethod[]) {
      if (!known.has(d.name)) methods.oauth.push(d);
    }
  }
  if (passkey.status === "fulfilled" && passkey.value) {
    methods.passkey = passkey.value.enabled === true;
  }

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(methods));
  } catch {
    /* a full or disabled sessionStorage only costs us the cache */
  }
  return methods;
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ------------------------------------------------------------------ passkeys */

// WebAuthn speaks ArrayBuffers; the server speaks base64url. These two
// functions are the whole impedance mismatch, and getting either wrong produces
// a ceremony that fails with an opaque browser error rather than a useful one.

function b64urlToBuf(value: string): ArrayBuffer {
  const pad = value.length % 4 === 0 ? "" : "=".repeat(4 - (value.length % 4));
  const bin = atob(value.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Whether this browser can do WebAuthn at all. */
export function browserSupportsPasskeys(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    !!navigator.credentials
  );
}

/**
 * Sign in with a passkey.
 *
 * The server chooses the challenge and remembers it; the browser signs it with
 * a key bound to this origin. That origin binding is the whole point — a
 * lookalike domain cannot ask the authenticator for a signature that works here.
 */
export async function signInWithPasskey(email: string): Promise<void> {
  const beginRes = await fetch(`${PB_URL}/api/dpp/passkey/login/begin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!beginRes.ok) throw new Error("No passkey is registered for this account.");
  const options = (await beginRes.json()).publicKey;

  options.challenge = b64urlToBuf(options.challenge);
  for (const c of options.allowCredentials ?? []) c.id = b64urlToBuf(c.id);

  const cred = (await navigator.credentials.get({
    publicKey: options,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Sign-in was cancelled.");

  const resp = cred.response as AuthenticatorAssertionResponse;
  const finishRes = await fetch(`${PB_URL}/api/dpp/passkey/login/finish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufToB64url(resp.clientDataJSON),
        authenticatorData: bufToB64url(resp.authenticatorData),
        signature: bufToB64url(resp.signature),
        userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
      },
    }),
  });
  if (!finishRes.ok) {
    const body = await finishRes.json().catch(() => ({}));
    throw new Error(body.error || "Could not sign in with that passkey.");
  }
  const { token } = await finishRes.json();

  // Adopt the token as the session. The server issues the SAME token a password
  // login would, so everything downstream is unaware this was a passkey.
  pb.authStore.save(token, null);
  const me = await pb.collection(USERS_COLLECTION).authRefresh();
  pb.authStore.save(token, me.record);
}

/**
 * Register a passkey on the signed-in account.
 *
 * Additive: it never replaces the password or OTP path. Somebody locked out of
 * their own catalogue because they changed laptops is a worse outcome than a
 * second sign-in method existing.
 */
export async function registerPasskey(label: string): Promise<void> {
  const beginRes = await fetch(`${PB_URL}/api/dpp/passkey/register/begin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ label }),
  });
  if (!beginRes.ok) throw new Error("Could not start passkey registration.");
  const options = (await beginRes.json()).publicKey;

  options.challenge = b64urlToBuf(options.challenge);
  options.user.id = b64urlToBuf(options.user.id);
  for (const c of options.excludeCredentials ?? []) c.id = b64urlToBuf(c.id);

  const cred = (await navigator.credentials.create({
    publicKey: options,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Registration was cancelled.");

  const resp = cred.response as AuthenticatorAttestationResponse;
  const finishRes = await fetch(`${PB_URL}/api/dpp/passkey/register/finish`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufToB64url(resp.clientDataJSON),
        attestationObject: bufToB64url(resp.attestationObject),
        transports: resp.getTransports?.() ?? [],
      },
    }),
  });
  if (!finishRes.ok) {
    const body = await finishRes.json().catch(() => ({}));
    throw new Error(body.error || "Could not register that passkey.");
  }
}

export interface PasskeyCredential {
  id: string;
  label: string;
  transports?: string;
  backedUp: boolean;
  createdAt?: string;
  lastUsedAt?: string;
}

export async function listPasskeys(): Promise<PasskeyCredential[]> {
  const res = await fetch(`${PB_URL}/api/dpp/passkey/credentials`, {
    headers: { Authorization: pb.authStore.token },
  });
  if (!res.ok) return [];
  return (await res.json()).credentials ?? [];
}

export async function deletePasskey(id: string): Promise<void> {
  const res = await fetch(`${PB_URL}/api/dpp/passkey/credentials/${id}`, {
    method: "DELETE",
    headers: { Authorization: pb.authStore.token },
  });
  if (!res.ok) throw new Error("Could not remove that passkey.");
}

/* ----------------------------------------------------------------------- OTP */

/** Ask for a one-time code. Returns the id the code is checked against. */
export async function requestOtp(email: string): Promise<string> {
  const req = await pb.collection(USERS_COLLECTION).requestOTP(email);
  return req.otpId;
}

export async function signInWithOtp(otpId: string, code: string): Promise<void> {
  await pb.collection(USERS_COLLECTION).authWithOTP(otpId, code);
}

/* --------------------------------------------------------------------- OAuth2 */

export async function signInWithOAuth(provider: string): Promise<void> {
  await pb.collection(USERS_COLLECTION).authWithOAuth2({ provider });
}
